import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { Button, Spinner, Alert } from 'reactstrap';
import {
  createIndividualCodePayment,
  confirmIndividualCodePayment,
} from 'redux/GroupOrders/GroupOrdersActions';
import { getUserData } from 'redux/UserAuthentication/LoginActions';

const IndividualCodePaymentForm = ({ code, priceAmount, planId, savedCards, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();

  const [selectedCard, setSelectedCard] = useState(savedCards?.length > 0 ? savedCards[0].id : 'new');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (selectedCard !== 'new') {
        // Saved card flow
        const data = await dispatch(createIndividualCodePayment(code, selectedCard));

        if (data.requiresAction) {
          // 3DS required
          const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
            clientSecret: data.clientSecret,
            confirmParams: {
              return_url: window.location.origin + '/client/plans',
            },
            redirect: 'if_required',
          });
          if (confirmError) {
            setError(confirmError.message);
            setLoading(false);
            return;
          }
          // Confirm on backend
          const confirmData = await dispatch(confirmIndividualCodePayment(code, paymentIntent.id));
          dispatch(getUserData({ onLogin: true }));
          onSuccess(confirmData);
        } else if (data.success) {
          dispatch(getUserData({ onLogin: true }));
          onSuccess(data);
        }
      } else {
        // New card flow
        if (!stripe || !elements) {
          setError('Payment system is still loading.');
          setLoading(false);
          return;
        }

        const { error: submitError } = await elements.submit();
        if (submitError) {
          setError(submitError.message);
          setLoading(false);
          return;
        }

        // Get clientSecret from backend
        const data = await dispatch(createIndividualCodePayment(code, undefined));

        if (data.success) {
          // Saved card was auto-used (shouldn't happen in new card flow, but handle)
          dispatch(getUserData({ onLogin: true }));
          onSuccess(data);
          return;
        }

        // Confirm payment with Stripe
        const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
          elements,
          clientSecret: data.clientSecret,
          confirmParams: {
            return_url: window.location.origin + '/client/plans',
          },
          redirect: 'if_required',
        });

        if (confirmError) {
          setError(confirmError.message);
          setLoading(false);
          return;
        }

        // Confirm on backend
        const confirmData = await dispatch(confirmIndividualCodePayment(code, paymentIntent.id));
        dispatch(getUserData({ onLogin: true }));
        onSuccess(confirmData);
      }
    } catch (err) {
      console.error('Individual code payment error:', err);
      setError(err.response?.data?.error || err.message || 'Payment failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-3 text-center">
        <h5 className="mb-1">Group Discount Price</h5>
        <span className="h2 text-success">${priceAmount}</span>
      </div>

      {savedCards && savedCards.length > 0 && (
        <div className="mb-3">
          <label className="font-weight-bold d-block mb-2" style={{ fontSize: '0.9rem' }}>Payment Method</label>
          {savedCards.map((card) => (
            <div
              key={card.id}
              className={`border rounded p-2 mb-2 d-flex align-items-center ${selectedCard === card.id ? 'border-primary bg-secondary' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => setSelectedCard(card.id)}
            >
              <input
                type="radio"
                name="paymentMethod"
                checked={selectedCard === card.id}
                onChange={() => setSelectedCard(card.id)}
                className="mr-2"
              />
              <i className={`fab fa-cc-${card.card?.brand || 'credit-card'} mr-2`} style={{ fontSize: '1.3rem' }} />
              <span style={{ fontSize: '0.9rem' }}>
                {card.card?.brand?.toUpperCase() || 'Card'} ending in {card.card?.last4}
              </span>
              {card.isDefault && <small className="ml-auto text-muted">Default</small>}
            </div>
          ))}
          <div
            className={`border rounded p-2 d-flex align-items-center ${selectedCard === 'new' ? 'border-primary bg-secondary' : ''}`}
            style={{ cursor: 'pointer' }}
            onClick={() => setSelectedCard('new')}
          >
            <input
              type="radio"
              name="paymentMethod"
              checked={selectedCard === 'new'}
              onChange={() => setSelectedCard('new')}
              className="mr-2"
            />
            <i className="fas fa-plus-circle mr-2 text-primary" />
            <span style={{ fontSize: '0.9rem' }}>Use a new card</span>
          </div>
        </div>
      )}

      {selectedCard === 'new' && (
        <div className="mb-3">
          <PaymentElement />
        </div>
      )}

      {error && (
        <Alert color="danger" className="mb-3" style={{ fontSize: '0.85rem' }}>
          {error}
        </Alert>
      )}

      <Button
        color="success"
        type="submit"
        className="w-100"
        size="lg"
        disabled={loading || (!stripe && selectedCard === 'new')}
      >
        {loading ? (
          <><Spinner size="sm" className="mr-2" />Processing...</>
        ) : (
          <><i className="fas fa-lock mr-2" />Pay ${priceAmount}</>
        )}
      </Button>
    </form>
  );
};

export default IndividualCodePaymentForm;
